import React, { Fragment } from "react";
import styled from "styled-components";
import { usePlayersContext } from "./Common/Contexts";
import { useTranslationContext } from "../Hooks/useTranslationContext";
import PlayerName from "./PlayerName";
import { HeaderCell, Cell } from "./Common/Table";
import Answer from "../Types/Answer";
import Player from "../Types/Player";

interface ScoreBoardWithPointsThisTurnProps {
  answers: { [socketId: string]: Answer[] };
  handleOnRemovePlayer: (socketId: string) => void;
  gameSocketIds: string[];
  playerIdThatFinishedRound?: string;
}

const FlexContainer = styled.div`
  display: flex;
  margin: 1em 0;
  justify-content: center;
`;
const Container = styled.div`
  display: grid;
  grid-template-columns: minmax(5em, 1fr) minmax(5em, 1fr);
`;
const RelativeDiv = styled.div`
  position: relative;
  text-align: start;
`;
const SmallerSize = styled.span`
  font-size: 0.7em;
`;

export const ScoreBoardWithPointsThisTurn = ({
  gameSocketIds,
  answers,
  handleOnRemovePlayer,
  playerIdThatFinishedRound,
}: ScoreBoardWithPointsThisTurnProps) => {
  const { players } = usePlayersContext();
  const gamePlayers: { [socketId: string]: Player } = gameSocketIds
    .map((socketId) => ({ socketId, player: players[socketId] }))
    .filter(({ player }) => player !== undefined)
    .reduce((dict, { player, socketId }) => {
      const pointsThisTurn =
        answers[socketId]
          ?.map((a) => a.point)
          .reduce((sum: number, p) => sum + (p ?? 0), 0) ?? 0;
      return {
        ...dict,
        [socketId]: {
          ...player,
          pointsThisTurn,
          totalScore: player.totalScore + pointsThisTurn,
        },
      };
    }, {});
  return (
    <ScoreBoard
      gamePlayers={gamePlayers}
      handleOnRemovePlayer={handleOnRemovePlayer}
      playerIdThatFinishedRound={playerIdThatFinishedRound}
    />
  );
};

interface ScoreBoardProps {
  gamePlayers: { [socketId: string]: Player };
  handleOnRemovePlayer?: (socketId: string) => void;
  playerIdThatFinishedRound?: string;
}

export const ScoreBoard = ({
  gamePlayers,
  handleOnRemovePlayer,
  playerIdThatFinishedRound,
}: ScoreBoardProps) => {
  const { t } = useTranslationContext();
  return (
    <FlexContainer>
      <Container data-test="scoreboardContainer">
        <HeaderCell></HeaderCell>

        <HeaderCell>{t["total.points"]}</HeaderCell>
        {Object.entries(gamePlayers)
          .sort(([, p1], [, p2]) => p2.totalScore - p1.totalScore ?? 0)
          .map(([socketId, player]) => (
            <Fragment key={socketId}>
              <RelativeDiv>
                <PlayerName
                  name={player.name}
                  handleOnRemovePlayer={() =>
                    handleOnRemovePlayer && handleOnRemovePlayer(socketId)
                  }
                  isConnected={player.isConnected}
                  isReady={player.isReady}
                  showIcons={!!handleOnRemovePlayer}
                  finishedFirst={socketId === playerIdThatFinishedRound}
                />
              </RelativeDiv>
              <Cell>
                {player.totalScore}
                {player.pointsThisTurn !== undefined && (
                  <SmallerSize> ({player.pointsThisTurn})</SmallerSize>
                )}
              </Cell>
            </Fragment>
          ))}
      </Container>
    </FlexContainer>
  );
};
